/**
 * UI-only demo rooms and bookings for provider scheduling (not shared rbac/mockData).
 * Used by RoomSelect and RoomAvailabilityDrawer; keys align with ProviderCalendarPage location values.
 */

export type DemoRoomType = 'therapy-room' | 'phone-booth';

export type DemoDeviceStatus = 'active' | 'inactive';

export interface DemoDevice {
  id: string;
  locationId: string;
  name: string;
  portability: 'fixed' | 'portable';
  /** Room id when portability is fixed */
  homeRoomId?: string;
  status?: DemoDeviceStatus;
}

export interface DemoDeviceBooking {
  id: string;
  deviceId: string;
  locationId: string;
  startMinutes: number;
  endMinutes: number;
  providerName: string;
}

export interface DemoRoom {
  id: string;
  locationId: string;
  name: string;
  type: DemoRoomType;
}

export interface DemoBooking {
  id: string;
  roomId: string;
  locationId: string;
  /** Minutes from local midnight on the event day */
  startMinutes: number;
  endMinutes: number;
  providerName: string;
  clientInitials: string;
  kind: 'in-person' | 'telehealth';
}

export const DEMO_ROOMS: DemoRoom[] = [
  { id: 'mo-r101', locationId: 'main-office', name: 'Room 101', type: 'therapy-room' },
  { id: 'mo-r102', locationId: 'main-office', name: 'Room 102', type: 'therapy-room' },
  { id: 'mo-group-a', locationId: 'main-office', name: 'Group Room A', type: 'therapy-room' },
  { id: 'mo-booth-1', locationId: 'main-office', name: 'Phone Booth 1', type: 'phone-booth' },
  { id: 'mo-booth-2', locationId: 'main-office', name: 'Phone Booth 2', type: 'phone-booth' },
  { id: 'dt-r1', locationId: 'downtown-office', name: 'Suite 3A', type: 'therapy-room' },
  { id: 'dt-booth', locationId: 'downtown-office', name: 'Booth', type: 'phone-booth' },
  { id: 'ws-r1', locationId: 'westside-clinic', name: 'West 1', type: 'therapy-room' },
  { id: 'ws-group', locationId: 'westside-clinic', name: 'Group Studio', type: 'therapy-room' },
];

export const DEMO_DEVICES: DemoDevice[] = [
  {
    id: 'mo-dev-emdr',
    locationId: 'main-office',
    name: 'EMDR Lightbar #1',
    portability: 'fixed',
    homeRoomId: 'mo-r101',
    status: 'active',
  },
  {
    id: 'mo-dev-vr',
    locationId: 'main-office',
    name: 'Meta Quest 3 #1',
    portability: 'portable',
    status: 'active',
  },
  {
    id: 'mo-dev-bio',
    locationId: 'main-office',
    name: 'Biofeedback monitor',
    portability: 'portable',
    status: 'active',
  },
  {
    id: 'dt-dev-vr',
    locationId: 'downtown-office',
    name: 'Meta Quest 3 (downtown)',
    portability: 'portable',
    status: 'active',
  },
  {
    id: 'dt-dev-emdr',
    locationId: 'downtown-office',
    name: 'EMDR tappers',
    portability: 'fixed',
    homeRoomId: 'dt-r1',
    status: 'active',
  },
  {
    id: 'ws-dev-bio',
    locationId: 'westside-clinic',
    name: 'Biofeedback kit',
    portability: 'portable',
    status: 'active',
  },
  {
    id: 'ws-dev-vr',
    locationId: 'westside-clinic',
    name: 'VR headset',
    portability: 'portable',
    status: 'active',
  },
  {
    id: 'ws-dev-emdr',
    locationId: 'westside-clinic',
    name: 'EMDR lightbar',
    portability: 'fixed',
    homeRoomId: 'ws-r1',
    status: 'active',
  },
];

/** Portable device conflicts — same pattern every calendar day */
export const DEMO_DEVICE_BOOKINGS_TEMPLATE: Omit<DemoDeviceBooking, 'id'>[] = [
  {
    deviceId: 'mo-dev-vr',
    locationId: 'main-office',
    startMinutes: 10 * 60,
    endMinutes: 11 * 60,
    providerName: 'Dr. Chen',
  },
  {
    deviceId: 'mo-dev-vr',
    locationId: 'main-office',
    startMinutes: 14 * 60,
    endMinutes: 15 * 60 + 30,
    providerName: 'Dr. Nguyen',
  },
  {
    deviceId: 'mo-dev-bio',
    locationId: 'main-office',
    startMinutes: 9 * 60,
    endMinutes: 10 * 60 + 30,
    providerName: 'Dr. Patel',
  },
  {
    deviceId: 'dt-dev-vr',
    locationId: 'downtown-office',
    startMinutes: 11 * 60,
    endMinutes: 12 * 60 + 30,
    providerName: 'Dr. Lee',
  },
  {
    deviceId: 'ws-dev-bio',
    locationId: 'westside-clinic',
    startMinutes: 10 * 60,
    endMinutes: 11 * 60,
    providerName: 'Dr. Thompson',
  },
  {
    deviceId: 'ws-dev-vr',
    locationId: 'westside-clinic',
    startMinutes: 15 * 60,
    endMinutes: 16 * 60 + 30,
    providerName: 'Dr. Thompson',
  },
];

/** Same busy pattern every calendar day (minutes from midnight, local) */
export const DEMO_BOOKINGS_TEMPLATE: Omit<DemoBooking, 'id'>[] = [
  // ── Main office — Room 101 ──
  { roomId: 'mo-r101', locationId: 'main-office', startMinutes: 8 * 60, endMinutes: 9 * 60, providerName: 'Dr. Nguyen', clientInitials: 'T.W.', kind: 'in-person' },
  { roomId: 'mo-r101', locationId: 'main-office', startMinutes: 9 * 60, endMinutes: 10 * 60, providerName: 'Dr. Chen', clientInitials: 'J.D.', kind: 'in-person' },
  { roomId: 'mo-r101', locationId: 'main-office', startMinutes: 10 * 60 + 30, endMinutes: 11 * 60 + 30, providerName: 'Dr. Chen', clientInitials: 'P.S.', kind: 'in-person' },
  { roomId: 'mo-r101', locationId: 'main-office', startMinutes: 13 * 60, endMinutes: 14 * 60, providerName: 'Dr. Nguyen', clientInitials: 'R.M.', kind: 'in-person' },
  { roomId: 'mo-r101', locationId: 'main-office', startMinutes: 15 * 60, endMinutes: 16 * 60, providerName: 'Dr. Chen', clientInitials: 'A.K.', kind: 'in-person' },
  { roomId: 'mo-r101', locationId: 'main-office', startMinutes: 16 * 60 + 30, endMinutes: 17 * 60 + 30, providerName: 'Dr. Nguyen', clientInitials: 'C.B.', kind: 'in-person' },

  // ── Main office — Room 102 ──
  { roomId: 'mo-r102', locationId: 'main-office', startMinutes: 8 * 60 + 30, endMinutes: 9 * 60 + 30, providerName: 'Dr. Patel', clientInitials: 'L.F.', kind: 'in-person' },
  { roomId: 'mo-r102', locationId: 'main-office', startMinutes: 10 * 60, endMinutes: 11 * 60, providerName: 'Dr. Patel', clientInitials: 'H.G.', kind: 'in-person' },
  { roomId: 'mo-r102', locationId: 'main-office', startMinutes: 11 * 60 + 30, endMinutes: 12 * 60 + 30, providerName: 'Dr. Rivera', clientInitials: 'N.J.', kind: 'in-person' },
  { roomId: 'mo-r102', locationId: 'main-office', startMinutes: 14 * 60, endMinutes: 15 * 60, providerName: 'You', clientInitials: 'K.L.', kind: 'in-person' },
  { roomId: 'mo-r102', locationId: 'main-office', startMinutes: 15 * 60 + 30, endMinutes: 16 * 60 + 30, providerName: 'Dr. Rivera', clientInitials: 'S.T.', kind: 'in-person' },

  // ── Main office — Group Room A ──
  { roomId: 'mo-group-a', locationId: 'main-office', startMinutes: 9 * 60, endMinutes: 10 * 60 + 30, providerName: 'Dr. Rivera', clientInitials: 'GRP', kind: 'in-person' },
  { roomId: 'mo-group-a', locationId: 'main-office', startMinutes: 13 * 60, endMinutes: 14 * 60 + 30, providerName: 'Dr. Chen', clientInitials: 'GRP', kind: 'in-person' },
  { roomId: 'mo-group-a', locationId: 'main-office', startMinutes: 17 * 60, endMinutes: 18 * 60 + 30, providerName: 'Dr. Rivera', clientInitials: 'GRP', kind: 'in-person' },

  // ── Main office — Phone Booth 1 ──
  { roomId: 'mo-booth-1', locationId: 'main-office', startMinutes: 8 * 60, endMinutes: 9 * 60, providerName: 'Dr. Patel', clientInitials: 'E.Q.', kind: 'telehealth' },
  { roomId: 'mo-booth-1', locationId: 'main-office', startMinutes: 9 * 60 + 30, endMinutes: 10 * 60 + 30, providerName: 'Dr. Kim', clientInitials: 'W.Y.', kind: 'telehealth' },
  { roomId: 'mo-booth-1', locationId: 'main-office', startMinutes: 11 * 60, endMinutes: 12 * 60, providerName: 'Dr. Patel', clientInitials: 'M.R.', kind: 'telehealth' },
  { roomId: 'mo-booth-1', locationId: 'main-office', startMinutes: 13 * 60, endMinutes: 14 * 60, providerName: 'Dr. Kim', clientInitials: 'D.A.', kind: 'telehealth' },
  { roomId: 'mo-booth-1', locationId: 'main-office', startMinutes: 15 * 60, endMinutes: 16 * 60, providerName: 'You', clientInitials: 'B.C.', kind: 'telehealth' },

  // ── Main office — Phone Booth 2 ──
  { roomId: 'mo-booth-2', locationId: 'main-office', startMinutes: 9 * 60, endMinutes: 10 * 60, providerName: 'Dr. Nguyen', clientInitials: 'F.H.', kind: 'telehealth' },
  { roomId: 'mo-booth-2', locationId: 'main-office', startMinutes: 10 * 60 + 30, endMinutes: 11 * 60 + 30, providerName: 'Dr. Kim', clientInitials: 'G.P.', kind: 'telehealth' },
  { roomId: 'mo-booth-2', locationId: 'main-office', startMinutes: 14 * 60, endMinutes: 15 * 60, providerName: 'Dr. Rivera', clientInitials: 'J.V.', kind: 'telehealth' },
  { roomId: 'mo-booth-2', locationId: 'main-office', startMinutes: 16 * 60, endMinutes: 17 * 60, providerName: 'Dr. Kim', clientInitials: 'U.Z.', kind: 'telehealth' },

  // ── Downtown office — Suite 3A ──
  { roomId: 'dt-r1', locationId: 'downtown-office', startMinutes: 9 * 60, endMinutes: 10 * 60, providerName: 'Dr. Lee', clientInitials: 'A.B.', kind: 'in-person' },
  { roomId: 'dt-r1', locationId: 'downtown-office', startMinutes: 10 * 60 + 30, endMinutes: 11 * 60 + 30, providerName: 'Dr. Lee', clientInitials: 'C.D.', kind: 'in-person' },
  { roomId: 'dt-r1', locationId: 'downtown-office', startMinutes: 12 * 60, endMinutes: 13 * 60, providerName: 'Dr. Garcia', clientInitials: 'E.F.', kind: 'in-person' },
  { roomId: 'dt-r1', locationId: 'downtown-office', startMinutes: 14 * 60, endMinutes: 15 * 60, providerName: 'Dr. Lee', clientInitials: 'G.H.', kind: 'in-person' },
  { roomId: 'dt-r1', locationId: 'downtown-office', startMinutes: 15 * 60 + 30, endMinutes: 16 * 60 + 30, providerName: 'Dr. Garcia', clientInitials: 'I.J.', kind: 'in-person' },
  { roomId: 'dt-r1', locationId: 'downtown-office', startMinutes: 17 * 60, endMinutes: 18 * 60, providerName: 'Dr. Lee', clientInitials: 'K.L.', kind: 'in-person' },

  // ── Downtown office — Booth ──
  { roomId: 'dt-booth', locationId: 'downtown-office', startMinutes: 8 * 60 + 30, endMinutes: 9 * 60 + 30, providerName: 'Dr. Garcia', clientInitials: 'M.N.', kind: 'telehealth' },
  { roomId: 'dt-booth', locationId: 'downtown-office', startMinutes: 10 * 60, endMinutes: 11 * 60, providerName: 'Dr. Garcia', clientInitials: 'O.P.', kind: 'telehealth' },
  { roomId: 'dt-booth', locationId: 'downtown-office', startMinutes: 13 * 60, endMinutes: 14 * 60, providerName: 'Dr. Lee', clientInitials: 'Q.R.', kind: 'telehealth' },
  { roomId: 'dt-booth', locationId: 'downtown-office', startMinutes: 15 * 60, endMinutes: 16 * 60, providerName: 'Dr. Garcia', clientInitials: 'S.T.', kind: 'telehealth' },

  // ── Westside clinic — West 1 ──
  { roomId: 'ws-r1', locationId: 'westside-clinic', startMinutes: 9 * 60, endMinutes: 10 * 60, providerName: 'Dr. Thompson', clientInitials: 'A.Z.', kind: 'in-person' },
  { roomId: 'ws-r1', locationId: 'westside-clinic', startMinutes: 10 * 60 + 30, endMinutes: 11 * 60 + 30, providerName: 'Dr. Thompson', clientInitials: 'B.Y.', kind: 'in-person' },
  { roomId: 'ws-r1', locationId: 'westside-clinic', startMinutes: 13 * 60, endMinutes: 14 * 60, providerName: 'You', clientInitials: 'C.X.', kind: 'in-person' },
  { roomId: 'ws-r1', locationId: 'westside-clinic', startMinutes: 15 * 60, endMinutes: 16 * 60, providerName: 'Dr. Thompson', clientInitials: 'D.W.', kind: 'in-person' },
  { roomId: 'ws-r1', locationId: 'westside-clinic', startMinutes: 16 * 60 + 30, endMinutes: 17 * 60 + 30, providerName: 'Dr. Thompson', clientInitials: 'E.V.', kind: 'in-person' },

  // ── Westside clinic — Group Studio ──
  { roomId: 'ws-group', locationId: 'westside-clinic', startMinutes: 10 * 60, endMinutes: 11 * 60 + 30, providerName: 'Dr. Thompson', clientInitials: 'GRP', kind: 'in-person' },
  { roomId: 'ws-group', locationId: 'westside-clinic', startMinutes: 14 * 60, endMinutes: 15 * 60 + 30, providerName: 'You', clientInitials: 'GRP', kind: 'in-person' },
];

export function getDemoBookingsForDate(date: Date): DemoBooking[] {
  const dayKey = date.toDateString();
  return DEMO_BOOKINGS_TEMPLATE.map((b, i) => ({
    ...b,
    id: `${dayKey}-${i}`,
  }));
}

export function getRoomsForLocation(locationId: string, variant: 'session' | 'booth'): DemoRoom[] {
  return DEMO_ROOMS.filter((r) => {
    if (r.locationId !== locationId) return false;
    if (variant === 'booth') return r.type === 'phone-booth';
    return r.type === 'therapy-room';
  });
}

export function getDemoDeviceBookingsForDate(date: Date): DemoDeviceBooking[] {
  const dayKey = date.toDateString();
  return DEMO_DEVICE_BOOKINGS_TEMPLATE.map((b, i) => ({
    ...b,
    id: `${dayKey}-d${i}`,
  }));
}

export function deviceBookingToInterval(
  date: Date,
  b: DemoDeviceBooking,
): { start: Date; end: Date } {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setMinutes(b.startMinutes);
  const end = new Date(date);
  end.setHours(0, 0, 0, 0);
  end.setMinutes(b.endMinutes);
  return { start, end };
}

export function deviceIsAvailable(
  deviceId: string,
  locationId: string,
  eventDate: Date,
  windowStart: Date,
  windowEnd: Date,
): boolean {
  const bookings = getDemoDeviceBookingsForDate(eventDate).filter(
    (b) => b.deviceId === deviceId && b.locationId === locationId,
  );
  for (const b of bookings) {
    const { start, end } = deviceBookingToInterval(eventDate, b);
    if (intervalsOverlap(windowStart, windowEnd, start, end)) return false;
  }
  return true;
}

export function getPortableDevicesForLocation(locationId: string): DemoDevice[] {
  return DEMO_DEVICES.filter(
    (d) =>
      d.locationId === locationId &&
      d.portability === 'portable' &&
      (d.status ?? 'active') !== 'inactive',
  );
}

export function getFixedDevicesInRoom(locationId: string, roomId: string): DemoDevice[] {
  return DEMO_DEVICES.filter(
    (d) =>
      d.locationId === locationId &&
      d.portability === 'fixed' &&
      d.homeRoomId === roomId &&
      (d.status ?? 'active') !== 'inactive',
  );
}

export function intervalsOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): boolean {
  return aStart.getTime() < bEnd.getTime() && aEnd.getTime() > bStart.getTime();
}

/** Minutes-from-midnight interval (inclusive start, exclusive end for gaps). */
export type MinuteInterval = { startMinutes: number; endMinutes: number };

/** Merge overlapping or touching busy intervals (sorted, coalesced). */
export function mergeBusyIntervals(intervals: MinuteInterval[]): MinuteInterval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.startMinutes - b.startMinutes);
  const merged: MinuteInterval[] = [];
  let cur = { ...sorted[0] };
  for (let i = 1; i < sorted.length; i += 1) {
    const next = sorted[i];
    if (next.startMinutes <= cur.endMinutes) {
      cur.endMinutes = Math.max(cur.endMinutes, next.endMinutes);
    } else {
      merged.push(cur);
      cur = { ...next };
    }
  }
  merged.push(cur);
  return merged;
}

/**
 * Free gaps inside [windowStart, windowEnd] given busy intervals (may extend outside window).
 */
export function freeIntervalsWithinWindow(
  busy: MinuteInterval[],
  windowStart: number,
  windowEnd: number,
): MinuteInterval[] {
  const clamped = busy
    .map((b) => ({
      startMinutes: Math.max(b.startMinutes, windowStart),
      endMinutes: Math.min(b.endMinutes, windowEnd),
    }))
    .filter((b) => b.endMinutes > b.startMinutes);
  const merged = mergeBusyIntervals(clamped);
  const free: MinuteInterval[] = [];
  let cursor = windowStart;
  for (const b of merged) {
    if (b.startMinutes > cursor) {
      free.push({ startMinutes: cursor, endMinutes: b.startMinutes });
    }
    cursor = Math.max(cursor, b.endMinutes);
  }
  if (cursor < windowEnd) {
    free.push({ startMinutes: cursor, endMinutes: windowEnd });
  }
  return free;
}

export function bookingToInterval(date: Date, b: DemoBooking): { start: Date; end: Date } {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setMinutes(b.startMinutes);
  const end = new Date(date);
  end.setHours(0, 0, 0, 0);
  end.setMinutes(b.endMinutes);
  return { start, end };
}

export const LOCATION_LABELS: Record<string, string> = {
  'main-office': 'Main office',
  'downtown-office': 'Downtown office',
  'westside-clinic': 'Westside clinic',
  telehealth: 'Remote',
};

export const OFFICE_LOCATION_OPTIONS = [
  { value: 'main-office', label: 'Main office — 123 Main St' },
  { value: 'downtown-office', label: 'Downtown office' },
  { value: 'westside-clinic', label: 'Westside clinic' },
];
